/* eslint-disable */
import { SimplePool, generateSecretKey, finalizeEvent } from 'nostr-tools';

// ── Nostr Relay Pool ─────────────────────────────────────────────────────────
// Multiple well-maintained public Nostr relays for redundancy.
// Signaling content is AES-GCM encrypted before publishing — relays see only ciphertext.
const NOSTR_RELAYS = [
  'wss://relay.damus.io',
  'wss://relay.nostr.band',
  'wss://nos.lol',
  'wss://offchain.pub',
  'wss://relay.snort.social',
];

// ── WebRTC Config ─────────────────────────────────────────────────────────────
const RTC_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun.cloudflare.com:3478' },
    // Free TURN servers for strict NAT traversal (media stays P2P after handshake)
    { urls: 'turn:openrelay.metered.ca:80',  username: 'openrelayproject', credential: 'openrelayproject' },
    { urls: 'turn:openrelay.metered.ca:443', username: 'openrelayproject', credential: 'openrelayproject' },
  ],
  iceCandidatePoolSize: 10,
};

// ── Singletons ────────────────────────────────────────────────────────────────
let _pool = null;
const getPool = () => { if (!_pool) _pool = new SimplePool(); return _pool; };

// Ephemeral signing key — used only for publishing Nostr events, not for identity
let _sigKey = null;
const getSigningKey = () => { if (!_sigKey) _sigKey = generateSecretKey(); return _sigKey; };

// Sessions declined by the user (can't delete Nostr events, so track in memory)
const declinedSessions = new Set();

// ── Crypto Helpers ────────────────────────────────────────────────────────────
const sha256hex = async (str) => {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
};

export const deriveCallKey = async (addr1, addr2) => {
  const [a, b] = [addr1, addr2].sort();
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(a + b + 'su-voice-salt-v1'));
  return new Uint8Array(hash);
};

// Ring key: derived from recipient address only — both sides can compute it
const deriveRingKey = async (recipientAddress) => {
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(recipientAddress + 'su-ring-key-v1'));
  return new Uint8Array(hash);
};

const encrypt = async (obj, keyBytes) => {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await crypto.subtle.importKey('raw', keyBytes, 'AES-GCM', false, ['encrypt']);
  const ct  = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(JSON.stringify(obj)));
  const out = new Uint8Array(12 + ct.byteLength);
  out.set(iv, 0); out.set(new Uint8Array(ct), 12);
  return btoa(String.fromCharCode(...out));
};

const decrypt = async (b64, keyBytes) => {
  const raw = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  const key = await crypto.subtle.importKey('raw', keyBytes, 'AES-GCM', false, ['decrypt']);
  const pt  = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: raw.slice(0, 12) }, key, raw.slice(12));
  return JSON.parse(new TextDecoder().decode(pt));
};

const randomId = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(10))).map(b => b.toString(16).padStart(2, '0')).join('');

// ── Nostr Pub/Sub Helpers ─────────────────────────────────────────────────────
const nostrPublish = (tag, content) => {
  try {
    const event = finalizeEvent({
      kind: 1,
      created_at: Math.floor(Date.now() / 1000),
      tags: [['t', tag]],
      content,
    }, getSigningKey());
    const pool = getPool();
    // Fire-and-forget to all relays — succeed if any accepts
    Promise.any(pool.publish(NOSTR_RELAYS, event)).catch(() => {});
  } catch (_) {}
};

const nostrSubscribe = (tag, onEvent, sinceSec) => {
  const seen = new Set();
  const pool = getPool();
  return pool.subscribeMany(
    NOSTR_RELAYS,
    [{ kinds: [1], '#t': [tag], since: sinceSec ?? (Math.floor(Date.now() / 1000) - 10) }],
    {
      onevent(event) {
        if (seen.has(event.id)) return; // deduplicate across relays
        seen.add(event.id);
        onEvent(event);
      },
    }
  );
};

// ── INITIATE CALL (caller side) ───────────────────────────────────────────────
export const initiateCall = async ({
  myAddress, recipientAddress, stream,
  onStream, onConnect, onClose, onError, onRinging,
}) => {
  if (!stream) throw new Error('No media stream provided');

  const sharedKey   = await deriveCallKey(myAddress, recipientAddress);
  const ringKey     = await deriveRingKey(recipientAddress);
  const sessionId   = randomId();
  const sigTag      = 'su_sig_' + sessionId;
  const recipientHash = (await sha256hex(recipientAddress)).slice(0, 24);
  const ringTag     = 'su_ring_' + recipientHash;

  const pc = new RTCPeerConnection(RTC_CONFIG);
  stream.getTracks().forEach(t => pc.addTrack(t, stream));

  pc.ontrack = (e) => { if (e.streams[0]) onStream(e.streams[0]); };
  pc.onconnectionstatechange = () => {
    const s = pc.connectionState;
    if (s === 'connected') onConnect();
    if (s === 'disconnected' || s === 'failed' || s === 'closed') { cleanup(); onClose(); }
  };

  // ICE candidates buffered until remote description is set
  const iceQueue = [];
  let remoteDescSet = false;

  pc.onicecandidate = async (e) => {
    if (!e.candidate) return;
    try {
      nostrPublish(sigTag, await encrypt({ type: 'ice_a', c: e.candidate.toJSON() }, sharedKey));
    } catch (_) {}
  };

  let answered = false;

  // Subscribe to signal channel — only live events (offer is already sent before any answer exists)
  const sub = nostrSubscribe(sigTag, async (event) => {
    if (!event.content) return;
    try {
      const d = await decrypt(event.content, sharedKey);
      if (d.type === 'answer' && !answered) {
        answered = true;
        await pc.setRemoteDescription(new RTCSessionDescription(d.sdp));
        remoteDescSet = true;
        for (const c of iceQueue) { try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch (_) {} }
        iceQueue.length = 0;
      } else if (d.type === 'ice_b') {
        if (remoteDescSet) { try { await pc.addIceCandidate(new RTCIceCandidate(d.c)); } catch (_) {} }
        else { iceQueue.push(d.c); }
      }
    } catch (_) {}
  }, Math.floor(Date.now() / 1000) - 5);

  // Create and publish offer
  const offer = await pc.createOffer({ offerToReceiveAudio: true });
  await pc.setLocalDescription(offer);
  nostrPublish(sigTag, await encrypt({ type: 'offer', sdp: { type: offer.type, sdp: offer.sdp } }, sharedKey));

  // Publish ring notification (encrypted with ring key)
  nostrPublish(ringTag, await encrypt({ from: myAddress, sessionId, ts: Date.now() }, ringKey));
  onRinging && onRinging();

  const ringTimeout = setTimeout(() => {
    if (!answered) { cleanup(); onError(new Error('No answer after 45s')); }
  }, 45000);

  const cleanup = () => {
    clearTimeout(ringTimeout);
    try { sub.close(); } catch (_) {}
    try { pc.close(); } catch (_) {}
  };

  return { pc, cleanup };
};

// ── ANSWER CALL (callee side) ─────────────────────────────────────────────────
export const answerCall = async ({
  myAddress, callerAddress, sessionId, stream,
  onStream, onConnect, onClose, onError,
}) => {
  if (!stream) throw new Error('No media stream provided');

  const sharedKey = await deriveCallKey(myAddress, callerAddress);
  const sigTag    = 'su_sig_' + sessionId;

  const pc = new RTCPeerConnection(RTC_CONFIG);
  stream.getTracks().forEach(t => pc.addTrack(t, stream));

  pc.ontrack = (e) => { if (e.streams[0]) onStream(e.streams[0]); };
  pc.onconnectionstatechange = () => {
    const s = pc.connectionState;
    if (s === 'connected') onConnect();
    if (s === 'disconnected' || s === 'failed' || s === 'closed') { cleanup(); onClose(); }
  };

  const iceQueue = [];
  let remoteDescSet = false;

  pc.onicecandidate = async (e) => {
    if (!e.candidate) return;
    try {
      nostrPublish(sigTag, await encrypt({ type: 'ice_b', c: e.candidate.toJSON() }, sharedKey));
    } catch (_) {}
  };

  let sub;
  const cleanup = () => {
    try { if (sub) sub.close(); } catch (_) {}
    try { pc.close(); } catch (_) {}
  };

  return new Promise((resolve, reject) => {
    let resolved = false;
    const timeout = setTimeout(() => {
      if (!resolved) { cleanup(); reject(new Error('Offer not received — caller may have hung up')); }
    }, 20000);

    // Subscribe with 60s lookback to catch the offer (published before callee accepted)
    sub = nostrSubscribe(sigTag, async (event) => {
      if (!event.content) return;
      try {
        const d = await decrypt(event.content, sharedKey);

        if (d.type === 'offer' && !resolved) {
          clearTimeout(timeout);
          await pc.setRemoteDescription(new RTCSessionDescription(d.sdp));
          remoteDescSet = true;
          for (const c of iceQueue) { try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch (_) {} }
          iceQueue.length = 0;
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          nostrPublish(sigTag, await encrypt({ type: 'answer', sdp: { type: answer.type, sdp: answer.sdp } }, sharedKey));
          resolved = true;
          resolve({ pc, cleanup });

        } else if (d.type === 'ice_a') {
          if (remoteDescSet) { try { await pc.addIceCandidate(new RTCIceCandidate(d.c)); } catch (_) {} }
          else { iceQueue.push(d.c); }
        }
      } catch (_) {}
    }, Math.floor(Date.now() / 1000) - 60);
  });
};

// ── INCOMING CALL LISTENER ────────────────────────────────────────────────────
export const listenForIncomingCalls = async (myAddress, onRing) => {
  const myHash  = (await sha256hex(myAddress)).slice(0, 24);
  const ringTag = 'su_ring_' + myHash;
  const ringKey = await deriveRingKey(myAddress);

  const sub = nostrSubscribe(ringTag, async (event) => {
    if (!event.content) return;
    try {
      const parsed = await decrypt(event.content, ringKey);
      if (!parsed.from || !parsed.sessionId) return;
      if (Date.now() - parsed.ts > 60000) return;      // ignore stale rings
      if (declinedSessions.has(parsed.sessionId)) return; // already declined
      onRing(parsed);
    } catch (_) {}
  }, Math.floor(Date.now() / 1000) - 30);

  return sub;
};

// ── CLEAR RING (after decline) ─────────────────────────────────────────────────
// Nostr events can't be deleted — we track declined sessions in memory instead
export const clearRing = (sessionId) => {
  if (sessionId) declinedSessions.add(sessionId);
};
